import cx from 'classnames'
import { useContext, useMemo, useState } from 'react'

import { type SanityCartSettings } from '@data/sanity/queries/types/cart'
import { CartContext } from '@lib/cart/context'
import { type ErrorMessages } from '@lib/request'
import { StringsContext } from '@lib/strings-context'

import CommentField from './comment-field'
import CartLineItems from './line-items'
import CartSubmit from './submit'
import CartSummary from './summary'
import VatIdField from './vat-id-field'

interface CartFormProps {
  cartSettings: SanityCartSettings
}

const CartForm = ({ cartSettings }: CartFormProps) => {
  const { cart, isCartUpdating, isCartSubmitting } = useContext(CartContext)
  const strings = useContext(StringsContext)

  const [vatId, setVatId] = useState('')
  const [comment, setComment] = useState('')
  const [errorMessages, setErrorMessages] = useState<ErrorMessages>({})

  const lineItems = useMemo(() => cart?.lineItems ?? [], [cart])
  const cartFormValues = useMemo(
    () => ({
      vatId,
      comment,
    }),
    [comment, vatId],
  )

  return (
    <div
      className={cx(
        'max-w-5xl w-full mx-auto px-[var(--container-padding)] py-10',
        {
          'cursor-wait': isCartUpdating || isCartSubmitting,
        },
      )}
    >
      <div className="flex flex-col relative h-full">
        <h1 className="is-h2 text-center mb-10">{strings.cartTitle}</h1>

        <div className="flex-1 flex flex-col">
          <CartLineItems lineItems={lineItems} />
        </div>

        {lineItems.length > 0 && (
          <div>
            <CartSummary />

            {!!cartSettings.showVatId && (
              <VatIdField
                errorMessages={errorMessages}
                setErrorMessages={setErrorMessages}
                onChange={(value) => setVatId(value)}
              />
            )}

            {!!cartSettings.showComment && (
              <CommentField onChange={(value) => setComment(value)} />
            )}

            <CartSubmit
              cartSettings={cartSettings}
              cartFormValues={cartFormValues}
              setErrorMessages={setErrorMessages}
              className="mt-7"
            >
              {strings.cartSubmit}
            </CartSubmit>

            {!!cartSettings.message && (
              <p className="mt-4 text-center text-sm font-medium">
                {cartSettings.message}
              </p>
            )}
          </div>
        )}
      </div>
    </div>
  )
}

export default CartForm
